import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

const WeeklyView = ({
  currentWeekStart,
  reminders,
  onNavigateWeek,
  goToCurrentWeek,
  isCurrentWeek,
  onEditReminder
}) => {
  const WEEKDAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const todayStr = new Date().toISOString().split("T")[0];
  
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(currentWeekStart);
    day.setDate(currentWeekStart.getDate() + i);
    weekDays.push(day);
  }

  const weekEnd = new Date(currentWeekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const startStr = currentWeekStart.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  const endStr = weekEnd.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });

  return (
    <Card className="border border-orange-200 bg-orange-50/20 shadow-sm rounded-sm" data-testid="weekly-reminders">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-500" />
            Vista Settimanale
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateWeek(-1)}
              className="h-8 w-8 p-0"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {!isCurrentWeek() && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToCurrentWeek}
                className="h-8 text-xs rounded-sm"
                data-testid="current-week-btn"
              >
                Oggi
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateWeek(1)}
              className="h-8 w-8 p-0"
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mt-1">
          {startStr} - {endStr}
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {weekDays.map((day, idx) => {
            const dateStr = day.toISOString().split("T")[0];
            const isToday = dateStr === todayStr;
            const isPast = dateStr < todayStr;
            const dayReminders = reminders.filter(r => 
              r.datetime_iso && 
              r.datetime_iso.startsWith(dateStr) && 
              !r.is_completed
            ).sort((a, b) => a.datetime_iso.localeCompare(b.datetime_iso));
            
            return (
              <div 
                key={dateStr}
                className={`min-h-[100px] sm:min-h-[120px] p-1 sm:p-2 rounded-sm border transition-colors ${
                  isToday 
                    ? "border-orange-400 bg-orange-100/50" 
                    : isPast
                      ? "border-gray-200 bg-gray-50/50"
                      : "border-orange-200 bg-white"
                }`}
              >
                <div className={`text-center mb-1 sm:mb-2 pb-1 border-b ${isToday ? "border-orange-300" : "border-orange-200"} ${isToday ? "font-bold" : ""}`}>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground">{WEEKDAY_NAMES[idx]}</p>
                  <p className={`text-xs sm:text-sm font-mono ${
                    isToday ? "text-orange-600" : isPast ? "text-gray-400" : ""
                  }`}>
                    {day.getDate()}
                  </p>
                </div>
                
                <div className="space-y-0.5 sm:space-y-1">
                  {dayReminders.length > 0 ? (
                    dayReminders.slice(0, 3).map(reminder => {
                      const time = new Date(reminder.datetime_iso);
                      return (
                        <div 
                          key={reminder.reminder_id}
                          className={`p-1 sm:p-1.5 rounded text-[9px] sm:text-[10px] cursor-pointer transition-colors ${
                            isPast 
                              ? "bg-gray-100 hover:bg-gray-200" 
                              : "bg-orange-100 hover:bg-orange-200"
                          }`}
                          onClick={() => onEditReminder(reminder)}
                          title={reminder.description || reminder.title}
                        >
                          <p className="font-medium truncate">{reminder.title}</p>
                          <p className={isPast ? "text-gray-500" : "text-orange-600"}>
                            {time.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-[9px] sm:text-[10px] text-muted-foreground text-center py-1 sm:py-2">-</p>
                  )}
                  {dayReminders.length > 3 && (
                    <p className="text-[9px] sm:text-[10px] text-orange-600 text-center">
                      +{dayReminders.length - 3}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="flex justify-center gap-4 mt-3 text-[10px] text-muted-foreground">
          <span>Clicca su un promemoria per modificarlo</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyView;
